import { OrganizationAuthMethodEnum } from 'src/api/main'
import type { SocialProviderId } from 'src/features/auth/providers'

type ProviderInstructionHintKey =
  | 'authMethods.googleSetupHint'
  | 'authMethods.entraidSetupHint'
  | 'authMethods.appleSetupHint'

type RedirectUrlGroupLabelKey =
  | 'authMethods.backendApiRedirectUrls'
  | 'authMethods.mailboxRedirectUrls'

type ProviderInstructionConfig = {
  docsUrl: string
  callbackPaths: string[]
  mailboxCallbackPaths?: string[]
  hintKey: ProviderInstructionHintKey
}

type ProviderRedirectUrlGroup = {
  labelKey: RedirectUrlGroupLabelKey
  urls: string[]
}

export type ProviderSetupInstructions = {
  docsUrl: string
  redirectUrlGroups: ProviderRedirectUrlGroup[]
  hintKey: ProviderInstructionHintKey
}

const DEFAULT_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
const DEFAULT_API_ORIGIN = import.meta.env?.VITE_APP_CORE_API as
  | string
  | undefined

// `qr_login` is a social-enum member but not an OAuth provider — it has no
// setup instructions (docs URL / callback paths), so exclude it here.
type OAuthProviderId = Exclude<
  SocialProviderId,
  'email_otp' | 'passkey' | 'qr_login'
>

const providerInstructionConfig: Record<
  OAuthProviderId,
  ProviderInstructionConfig
> = {
  [OrganizationAuthMethodEnum.GOOGLE]: {
    docsUrl:
      'https://developers.google.com/identity/protocols/oauth2/web-server#creatingcred',
    // All Google flows go through a single backend callback that 302's to
    // the original frontend URL stored in the signed `state`.
    callbackPaths: ['/authorization/social/google/callback/'],
    hintKey: 'authMethods.googleSetupHint'
  },
  [OrganizationAuthMethodEnum.ENTRAID]: {
    docsUrl:
      'https://learn.microsoft.com/en-us/entra/identity-platform/reply-url',
    // Same single-backend-callback pattern for Microsoft Entra. The Outlook
    // mailbox connect is a separate backend callback (own OAuth state + PKCE),
    // so Entra needs both reply URLs registered or that flow fails with
    // AADSTS900971 while sign-in keeps working.
    callbackPaths: ['/authorization/social/entraid/callback/'],
    mailboxCallbackPaths: ['/integration/microsoft/graph/callback/'],
    hintKey: 'authMethods.entraidSetupHint'
  },
  [OrganizationAuthMethodEnum.APPLE]: {
    docsUrl:
      'https://developer.apple.com/documentation/sign_in_with_apple/configuring_your_environment_for_sign_in_with_apple',
    // Apple uses response_mode=form_post and the Return URL must point at the
    // backend form-callback endpoint, NOT at the SPA. The backend then 302s
    // back to the frontend with code/state in the query.
    callbackPaths: ['/authorization/social/apple/form-callback/'],
    hintKey: 'authMethods.appleSetupHint'
  }
}

const normalizeOrigin = (value: string): string | null => {
  const normalizedValue = value.trim()
  if (!normalizedValue) return null

  const withProtocol = /^https?:\/\//.test(normalizedValue)
    ? normalizedValue
    : `https://${normalizedValue}`

  try {
    return new URL(withProtocol).origin
  } catch {
    return null
  }
}

type SetupInstructionsOptions = {
  origin?: string
}

export const getProviderSetupInstructions = (
  provider: OrganizationAuthMethodEnum,
  options: SetupInstructionsOptions = {}
): ProviderSetupInstructions | null => {
  if (
    provider !== OrganizationAuthMethodEnum.GOOGLE &&
    provider !== OrganizationAuthMethodEnum.ENTRAID &&
    provider !== OrganizationAuthMethodEnum.APPLE
  ) {
    return null
  }

  const { origin = DEFAULT_ORIGIN } = options
  const config = providerInstructionConfig[provider]

  // All providers route through a single backend callback that 302's to the
  // original frontend URL stored in the signed `state`. The same backend URL
  // works for ALL organizations — the backend distinguishes them via the
  // state payload (org_id/domain_name + flow).
  const apiOrigin = normalizeOrigin(DEFAULT_API_ORIGIN ?? '') ?? origin

  return {
    docsUrl: config.docsUrl,
    redirectUrlGroups: [
      {
        labelKey: 'authMethods.backendApiRedirectUrls',
        urls: config.callbackPaths.map((path) => `${apiOrigin}${path}`)
      },
      ...(config.mailboxCallbackPaths
        ? [
            {
              labelKey: 'authMethods.mailboxRedirectUrls' as const,
              urls: config.mailboxCallbackPaths.map(
                (path) => `${apiOrigin}${path}`
              )
            }
          ]
        : [])
    ],
    hintKey: config.hintKey
  }
}
